import { useMemo, useState } from 'react';
import { useServices } from '../../hooks/useServices';
import { useGroups } from '../../hooks/useGroups';
import { checkboxClass } from '../common/Input';
import { NotificationTarget, TargetType } from '../../types';

interface Props {
  value: NotificationTarget[];
  onChange: (targets: NotificationTarget[]) => void;
  /** Wording for the "everything" option. */
  allLabel?: string;
  /** Wording for the scoped option. */
  someLabel?: string;
}

const key = (t: NotificationTarget) => `${t.target_type}:${t.target_id}`;

/**
 * Scope a channel or maintenance window to services, groups and/or tags.
 *
 * Used by both, since routing and suppression are the same question: "which
 * services does this apply to?" An empty list means all of them.
 */
export function TargetPicker({ value, onChange, allLabel = 'All services', someLabel = 'Only these' }: Props) {
  const { data: services } = useServices();
  const { data: groups } = useGroups();

  const tags = useMemo(() => {
    const set = new Set<string>();
    services?.forEach(s => s.tags?.forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [services]);

  // "Only these" with nothing ticked yet is a state the list alone cannot
  // express (an empty list means all), so the mode is held separately.
  const [scoped, setScoped] = useState(value.length > 0 && !value.some(t => t.target_type === 'all'));
  const isAll = !scoped;
  const selected = new Set(value.map(key));

  const toggle = (type: TargetType, id: string, on: boolean) => {
    const rest = value.filter(t => t.target_type !== 'all' && key(t) !== `${type}:${id}`);
    onChange(on ? [...rest, { target_type: type, target_id: id }] : rest);
  };

  const Section = ({ title, type, items }: { title: string; type: TargetType; items: { id: string; label: string }[] }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{title}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 max-h-32 overflow-y-auto">
          {items.map(i => (
            <label key={i.id} className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={selected.has(`${type}:${i.id}`)}
                onChange={e => toggle(type, i.id, e.target.checked)}
              />
              {i.label}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <fieldset className="space-y-3">
      <legend className="block text-sm font-medium mb-1">Applies to</legend>
      <div className="flex gap-4">
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            name="target-mode"
            className={checkboxClass}
            checked={isAll}
            onChange={() => { setScoped(false); onChange([]); }}
          />
          {allLabel}
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            name="target-mode"
            className={checkboxClass}
            checked={!isAll}
            onChange={() => { setScoped(true); onChange(value.filter(t => t.target_type !== 'all')); }}
          />
          {someLabel}
        </label>
      </div>

      {!isAll && (
        <div className="space-y-3 rounded-md border border-gray-200 dark:border-gray-700 p-3">
          <Section title="Services" type="service" items={(services ?? []).map(s => ({ id: String(s.id), label: s.name }))} />
          <Section title="Groups" type="group" items={(groups ?? []).map(g => ({ id: String(g.id), label: g.name }))} />
          <Section title="Tags" type="tag" items={tags.map(t => ({ id: t, label: t }))} />
          {value.length === 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400">Nothing selected yet — with no selection this applies to every service.</p>
          )}
        </div>
      )}
    </fieldset>
  );
}

/** "3 services, 1 group, tag production" — for list rows. */
export function describeTargets(
  targets: NotificationTarget[],
  lookup: { services?: Map<number, string>; groups?: Map<number, string> } = {},
): string {
  if (targets.length === 0 || targets.some(t => t.target_type === 'all')) return 'All services';
  const names = (type: TargetType, map?: Map<number, string>) =>
    targets.filter(t => t.target_type === type).map(t => map?.get(Number(t.target_id)) ?? t.target_id);
  const parts: string[] = [];
  const s = names('service', lookup.services);
  const g = names('group', lookup.groups);
  const tg = targets.filter(t => t.target_type === 'tag').map(t => t.target_id);
  if (s.length) parts.push(s.length <= 3 ? s.join(', ') : `${s.length} services`);
  if (g.length) parts.push(g.length <= 2 ? `group ${g.join(', ')}` : `${g.length} groups`);
  if (tg.length) parts.push(tg.length <= 2 ? `tag ${tg.join(', ')}` : `${tg.length} tags`);
  return parts.join(' · ');
}
